import { useQueryClient } from '@tanstack/react-query';
import { refreshClientToken, useClient } from 'hooks/useClient';
import { config } from 'config';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

// Every route in here is keyed on the email alone, so id is optional: the signin
// screen offers "continue with passkey" against a typed address, before there is
// any user object to hand over.
type PasskeyUser = {
  id?: number;
  email: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

export const usePasskeys = () => {
  const queryClient = useQueryClient();
  const authClient = useClient(config.AUTH_URL);

  const createPasskeyCredentials = async (user: PasskeyUser) => {
    // Get registration options from server
    const options = (await authClient.post(
      `/passkeys/challenge/${user.email}`,
    )) as PublicKeyCredentialCreationOptionsJSON;

    // Create passkey using browser API (handled by SimpleWebAuthn)
    const registrationResponse = await startRegistration({ optionsJSON: options });

    // Send full response to server for verification
    await authClient.post(`/passkeys/credentials`, registrationResponse);
    queryClient.invalidateQueries({ queryKey: ['CURRENT_USER'] });
  };

  const signinWithPasskey = async (user: PasskeyUser) => {
    // Get authentication options from server
    const options = (await authClient.get(`/passkeys/signin/${user.email}`)) as PublicKeyCredentialRequestOptionsJSON;

    // Authenticate using browser API (handled by SimpleWebAuthn)
    const authenticationResponse = await startAuthentication({ optionsJSON: options });

    // Send full response to server for verification
    const url = `/passkeys/verify/${user.email}`;
    const data = (await authClient.post(url, authenticationResponse)) as { token: string };
    localStorage.setItem('BEARER_TOKEN', data.token);
    // Before the refetch, as in signin: it asks /me straight away, with the client's token.
    refreshClientToken();
    queryClient.invalidateQueries({ queryKey: ['CURRENT_USER'] });
    return data;
  };

  const authorizeWithPasskey = async (user: PasskeyUser) => {
    // Get authentication options from server
    const options = (await authClient.get(`/passkeys/signin/${user.email}`)) as PublicKeyCredentialRequestOptionsJSON;

    // Authenticate using browser API (handled by SimpleWebAuthn)
    const authenticationResponse = await startAuthentication({ optionsJSON: options });

    // Return the credential ID for authorization purposes
    return authenticationResponse.id;
  };

  return { createPasskeyCredentials, signinWithPasskey, authorizeWithPasskey };
};
